import { get } from "lodash";
import { CustomVariableFormFields } from "pages/Recipe/components/CustomVariables/components/CustomVariableDialog";
import { findDictionaryItemByValue, getDefaultUnitsPrefix, parameterUnitsSearchVariants, recipeParameterUnitsType, superscriptValue, timeUnits, } from "shared/rsDictionary";
import { CutType, TimestampType, } from "shared/types";
import { getCutEventLabel, getSelectedTimestamp } from "store/rs-workspace/utils/insights.utils";
import { isNotEmpty } from "utils/validate";
import { CustomVariableFormulaType, CustomVariableRelativeValueType, CustomVariableRelativeValueVariableType, CustomVariableUnitFilterType, CustomVariableValueType, EventValueType, NoUnitsType, ParameterTypesCategoryType, } from "./recipe.types";
import { highlightMatchingText, renderNumberValue, sanitizeHtmlTags } from "utils/string";
import { getRecipesCustomVariables } from "store/rs-workspace/utils/scenario.utils";
export const CURRENT_TIME_TRANSFORMATION = "CurrentTime";
export var CustomVariableValueStatus;
(function (CustomVariableValueStatus) {
    CustomVariableValueStatus["Completed"] = "completed";
    CustomVariableValueStatus["Errored"] = "errored";
    CustomVariableValueStatus["Default"] = "default";
})(CustomVariableValueStatus || (CustomVariableValueStatus = {}));
const relativeValueParameterTypes = [
    CustomVariableRelativeValueVariableType.BioreactorParameter,
    CustomVariableRelativeValueVariableType.RecipeParameter,
];
const getRelativeValueType = (type) => {
    if (!type || type === CustomVariableRelativeValueVariableType.Unknown) {
        return;
    }
    return relativeValueParameterTypes.includes(type)
        ? CustomVariableRelativeValueType.Parameter
        : CustomVariableRelativeValueType.Variable;
};
export const isTwoValuesFormula = (type) => {
    const twoValuesFormulas = [
        CustomVariableFormulaType.Sum,
        CustomVariableFormulaType.Difference,
        CustomVariableFormulaType.Product,
        CustomVariableFormulaType.Minimum,
        CustomVariableFormulaType.Maximum,
        CustomVariableFormulaType.AbsoluteDifference,
        CustomVariableFormulaType.Quotient,
        CustomVariableFormulaType.Power,
    ];
    return !!type && twoValuesFormulas.includes(type);
};
export const isSingleValueFormula = (type) => {
    const singleValueFormulas = [
        CustomVariableFormulaType.Exponential,
        CustomVariableFormulaType.Value,
        CustomVariableFormulaType.LogBase10,
        CustomVariableFormulaType.Ln,
    ];
    return !!type && singleValueFormulas.includes(type);
};
const getRelativeVariableNameDictionary = (relativeVariableOptions, relativeParameterOptions, of) => {
    if (!of?.type) {
        return [];
    }
    const relativeValueType = getRelativeValueType(of?.type);
    if (!relativeValueType) {
        return [];
    }
    return relativeValueType === CustomVariableRelativeValueType.Variable
        ? relativeVariableOptions
        : relativeParameterOptions;
};
const getRelativeValueVariableValue = (of) => {
    if (!of?.type) {
        return "";
    }
    const relativeValueType = getRelativeValueType(of?.type);
    if (relativeValueType === CustomVariableRelativeValueType.Variable) {
        return of?.type === CustomVariableRelativeValueVariableType.CustomVariable
            ? of?.variableId
            : of?.variableType;
    }
    if (relativeValueType === CustomVariableRelativeValueType.Parameter) {
        return of?.type === CustomVariableRelativeValueVariableType.BioreactorParameter
            ? of?.parameterType
            : of?.parameterId;
    }
};
export const getCustomVariableRelativeValueUnitOption = (type, units) => {
    if ([
        CustomVariableRelativeValueVariableType.ProcessVariable,
        CustomVariableRelativeValueVariableType.RecipeParameter,
        CustomVariableRelativeValueVariableType.BioreactorParameter,
    ].includes(type)) {
        return recipeParameterUnitsType.find((item) => item.value === units);
    }
    if (type === CustomVariableRelativeValueVariableType.CustomVariable) {
        return { value: parseComplexUnitToHtml(units), label: parseComplexUnitToHtml(units) };
    }
    return { value: "", label: "" };
};
const getInitialFormulaValueUnit = (formulaValue, relativeVariableOptions, relativeParameterOptions) => {
    let variableUnit = "";
    const of = formulaValue?.of;
    if (of?.type === CustomVariableRelativeValueVariableType.CustomVariable) {
        const variableEntity = relativeVariableOptions.find(({ value }) => value?.value === of?.variableId);
        variableUnit = parseComplexUnitToHtml(variableEntity?.value?.units) ?? "";
    }
    if (of?.type === CustomVariableRelativeValueVariableType.ProcessVariable) {
        const variableEntity = relativeVariableOptions.find(({ value }) => value?.value === of?.variableType);
        variableUnit = recipeParameterUnitsType.find((item) => item.value === variableEntity?.units)?.label ?? "";
    }
    if (of?.type === CustomVariableRelativeValueVariableType.RecipeParameter) {
        const variableEntity = relativeParameterOptions.find(({ value }) => value?.value === of?.parameterId);
        variableUnit =
            recipeParameterUnitsType.find((item) => item.value === variableEntity?.value?.units)?.label ?? "";
    }
    if (of?.type === CustomVariableRelativeValueVariableType.BioreactorParameter) {
        const variableEntity = relativeParameterOptions.find(({ value }) => value?.value === of?.parameterType);
        variableUnit =
            recipeParameterUnitsType.find((item) => item.value === variableEntity?.value?.units)?.label ?? "";
    }
    return variableUnit;
};
const getFormulaValue = (formulaValue, relativeVariableOptions, relativeParameterOptions) => {
    const of = formulaValue?.of;
    const relativeValueVariableValue = getRelativeValueVariableValue(of);
    const relativeValueVariableLabel = getRelativeVariableNameDictionary(relativeVariableOptions, relativeParameterOptions, of).find(({ value }) => value?.value === getRelativeValueVariableValue(of))?.label ?? "";
    const variableName = isNotEmpty(relativeValueVariableValue, true) && isNotEmpty(relativeValueVariableLabel, true)
        ? { value: relativeValueVariableValue, label: relativeValueVariableLabel }
        : undefined;
    return {
        ...formulaValue,
        ...(formulaValue?.type === CustomVariableValueType.Relative
            ? {
                [CustomVariableFormFields.of]: {
                    ...of,
                    [CustomVariableFormFields._relativeValueType]: getRelativeValueType(of?.type),
                    [CustomVariableFormFields._variableName]: variableName,
                    [CustomVariableFormFields._variableUnit]: getInitialFormulaValueUnit(formulaValue, relativeVariableOptions, relativeParameterOptions),
                },
            }
            : {}),
    };
};
export const getCustomVariableDialogInitialValues = (currentCustomVariable, relativeVariableOptions, relativeParameterOptions, // TODO: fix typings
customVariableDefaultName) => {
    const initialValues = {
        [CustomVariableFormFields.formula]: {
            [CustomVariableFormFields.type]: CustomVariableFormulaType.Value,
        },
        [CustomVariableFormFields.recordInData]: false,
        [CustomVariableFormFields.note]: null,
        ...currentCustomVariable,
        [CustomVariableFormFields.name]: currentCustomVariable?.name ?? customVariableDefaultName,
    };
    const formulaType = currentCustomVariable?.formula?.type;
    let formulaValues = {};
    if (isTwoValuesFormula(formulaType)) {
        const formula = currentCustomVariable?.formula;
        formulaValues = {
            [CustomVariableFormFields.first]: {
                ...getFormulaValue(formula?.first, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.second]: {
                ...getFormulaValue(formula?.second, relativeVariableOptions, relativeParameterOptions),
            },
        };
    }
    if (isSingleValueFormula(formulaType)) {
        const formula = currentCustomVariable?.formula;
        formulaValues = {
            [CustomVariableFormFields.value]: {
                ...getFormulaValue(formula?.value, relativeVariableOptions, relativeParameterOptions),
            },
        };
    }
    if (formulaType === CustomVariableFormulaType.ZScore) {
        const formula = currentCustomVariable?.formula;
        formulaValues = {
            [CustomVariableFormFields.referenceValue]: {
                ...getFormulaValue(formula?.referenceValue, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.optimum]: {
                ...getFormulaValue(formula?.optimum, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.spread]: {
                ...getFormulaValue(formula?.spread, relativeVariableOptions, relativeParameterOptions),
            },
        };
    }
    if (formulaType === CustomVariableFormulaType.SaturationScore) {
        const formula = currentCustomVariable?.formula;
        formulaValues = {
            [CustomVariableFormFields.referenceValue]: {
                ...getFormulaValue(formula?.referenceValue, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.referenceLow]: {
                ...getFormulaValue(formula?.referenceLow, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.referenceHigh]: {
                ...getFormulaValue(formula?.referenceHigh, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.value50]: {
                ...getFormulaValue(formula?.value50, relativeVariableOptions, relativeParameterOptions),
            },
        };
    }
    if (formulaType === CustomVariableFormulaType.LogisticScore) {
        const formula = currentCustomVariable?.formula;
        formulaValues = {
            [CustomVariableFormFields.referenceValue]: {
                ...getFormulaValue(formula?.referenceValue, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.referenceLow]: {
                ...getFormulaValue(formula?.referenceLow, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.referenceHigh]: {
                ...getFormulaValue(formula?.referenceHigh, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.threshold]: {
                ...getFormulaValue(formula?.threshold, relativeVariableOptions, relativeParameterOptions),
            },
            [CustomVariableFormFields.sensitivity]: {
                ...getFormulaValue(formula?.sensitivity, relativeVariableOptions, relativeParameterOptions),
            },
        };
    }
    return {
        ...initialValues,
        [CustomVariableFormFields.formula]: {
            ...initialValues[CustomVariableFormFields.formula],
            ...formulaValues,
        },
    };
};
const getCustomVariableInPointInTime = (customVariable) => {
    if (!customVariable) {
        return false;
    }
    const formulaType = customVariable?.formula?.type;
    const checkIfPointInTime = (formulaValues) => formulaValues.every((item) => item?.transformationType === CutType.PointInTime);
    if (isTwoValuesFormula(formulaType)) {
        const formula = customVariable?.formula;
        return checkIfPointInTime([formula?.first, formula?.second]);
    }
    if (isSingleValueFormula(formulaType)) {
        const formula = customVariable?.formula;
        return checkIfPointInTime([formula?.value]);
    }
    if (formulaType === CustomVariableFormulaType.ZScore) {
        const formula = customVariable?.formula;
        return checkIfPointInTime([formula?.referenceValue, formula?.optimum, formula?.spread]);
    }
    if (formulaType === CustomVariableFormulaType.SaturationScore) {
        const formula = customVariable?.formula;
        return checkIfPointInTime([
            formula?.referenceValue,
            formula?.referenceHigh,
            formula?.referenceLow,
            formula?.value50,
        ]);
    }
    if (formulaType === CustomVariableFormulaType.LogisticScore) {
        const formula = customVariable?.formula;
        return checkIfPointInTime([
            formula?.referenceValue,
            formula?.referenceHigh,
            formula?.referenceLow,
            formula?.sensitivity,
            formula?.threshold,
        ]);
    }
    return false;
};
export const getValueTransformationsEnabled = (value, customVariables) => {
    const isRelativeValue = value?.type === CustomVariableValueType.Relative;
    const isRelativeValueProcessVariable = isRelativeValue &&
        value?.of?.type ===
            CustomVariableRelativeValueVariableType.ProcessVariable;
    const isRelativeValueCustomVariable = isRelativeValue &&
        value?.of?.type ===
            CustomVariableRelativeValueVariableType.CustomVariable;
    const transformationType = isRelativeValue && value?.transformationType;
    const selectedCustomVariableOf = value?.of;
    const selectedCustomVariableId = selectedCustomVariableOf?.variableId;
    const selectedCustomVariable = customVariables.find(({ id }) => id === selectedCustomVariableId);
    const isSelectedCustomVariableInPointInTime = isRelativeValueCustomVariable && getCustomVariableInPointInTime(selectedCustomVariable);
    const relativeValueFieldsConfigured = getFormulaValueRelativeValueFieldsConfiguredConditions(value).every((item) => isNotEmpty(item, true));
    const conditions = [
        isRelativeValueProcessVariable,
        relativeValueFieldsConfigured && !isSelectedCustomVariableInPointInTime,
    ];
    return !!transformationType && conditions.some(Boolean);
};
const getPointLabel = (timestampOptions, processVariables, cut, customVariables) => {
    let label = "";
    const timestampLabel = getSelectedTimestamp(timestampOptions, cut);
    const offsetUnits = findDictionaryItemByValue(timeUnits, cut?.offsetUnit)?.label;
    if (!processVariables) {
        return "";
    }
    if (timestampLabel) {
        label = timestampLabel;
    }
    if (isNotEmpty(cut?.offset) && offsetUnits) {
        const isCurrentTime = cut?.timestampType === TimestampType.CurrentTime;
        const value = isCurrentTime
            ? `-${cut?.offset} ${offsetUnits} of current time`
            : `(${cut?.offset} ${offsetUnits})`;
        const offsetLabel = cut?.offset === 0 ? "" : ` ${value}`;
        label = `${label}${offsetLabel}`;
    }
    return `${label}${getCutEventLabel(processVariables, cut?.cutEvent, customVariables)}`;
};
const getTransformationPointSummaryInfo = (cutInfo, timestampOptions, processVariables, customVariables) => {
    if (cutInfo?.type === CutType.PointInTime) {
        const { point } = { ...cutInfo };
        return getPointLabel(timestampOptions, processVariables, point, customVariables);
    }
    else {
        const { start, end } = { ...cutInfo };
        const startLabel = getPointLabel(timestampOptions, processVariables, start, customVariables);
        const endLabel = getPointLabel(timestampOptions, processVariables, end, customVariables);
        return `${startLabel ? `From: ${startLabel} -` : ""} ${endLabel ? `To: ${endLabel}` : ""}`;
    }
};
export const getFormulaValueTimingInfo = (formulaValue, timestampOptions, processVariables, transformationOptions, customVariables) => {
    const isRelativeValue = formulaValue?.type === CustomVariableValueType.Relative;
    const cutInfo = isRelativeValue ? formulaValue?.cut : undefined;
    const relativeValueTransformationType = formulaValue?.transformationType;
    const isCurrentTime = isRelativeValue && relativeValueTransformationType === CURRENT_TIME_TRANSFORMATION;
    if (!cutInfo && !isCurrentTime) {
        return "";
    }
    if (isCurrentTime) {
        return "Current time";
    }
    const transformationOption = transformationOptions
        ? findDictionaryItemByValue(transformationOptions, relativeValueTransformationType)
        : { value: "", label: "" };
    return `${transformationOption?.label}: ${getTransformationPointSummaryInfo(cutInfo, timestampOptions, processVariables, customVariables)}`;
};
export const getFormulaDisplayValue = (relativeVariableOptions, relativeParameterOptions, formulaValue) => {
    const isRelativeValue = formulaValue?.type === CustomVariableValueType.Relative;
    const displayValue = isRelativeValue
        ? formulaValue?.percentage
        : formulaValue?.value;
    const formulaOf = formulaValue?.of;
    const relativeValueSuffix = isRelativeValue
        ? `% of ${getRelativeVariableNameDictionary(relativeVariableOptions, relativeParameterOptions, formulaOf).find(({ value }) => value?.value === getRelativeValueVariableValue(formulaOf))?.label ?? ""}`
        : "";
    const absoluteValueUnit = findDictionaryItemByValue(recipeParameterUnitsType, formulaValue?.units, isNotEmpty(displayValue, true) ? getDefaultUnitsPrefix : undefined);
    const absoluteValueLabel = absoluteValueUnit?.value === NoUnitsType.NoUnits ? "" : ` ${absoluteValueUnit?.label ?? ""}`;
    return `${displayValue ? renderNumberValue(displayValue) : ""}${isRelativeValue ? relativeValueSuffix : absoluteValueLabel}`;
};
const getCutEventFilledConditions = (cutEvent, timestampType) => {
    let conditions = [cutEvent?.variable, cutEvent?.operation?.type];
    if (timestampType === TimestampType.CurrentTime || !cutEvent) {
        return [];
    }
    if (cutEvent?.operation?.type === EventValueType.OUT_OF_RANGE) {
        const { min, max } = { ...cutEvent?.operation };
        conditions = [...conditions, min?.value, max?.value, min?.units, max?.units];
    }
    else {
        const { value } = { ...cutEvent?.operation };
        conditions = [...conditions, value?.value, value?.units];
    }
    return conditions;
};
const getFormulaValueRelativeValueFieldsConfiguredConditions = (formulaValue) => {
    const value = formulaValue;
    const ofField = value?.of;
    const conditions = [value?.type, value?.percentage, ofField?.type];
    if (ofField?.type === CustomVariableRelativeValueVariableType.BioreactorParameter) {
        conditions.push(ofField?.parameterType);
    }
    if (ofField?.type === CustomVariableRelativeValueVariableType.RecipeParameter) {
        conditions.push(ofField?.parameterId);
    }
    if (ofField?.type === CustomVariableRelativeValueVariableType.ProcessVariable) {
        conditions.push(ofField?.variableType);
    }
    if (ofField?.type === CustomVariableRelativeValueVariableType.CustomVariable) {
        conditions.push(ofField?.variableId);
    }
    return conditions;
};
export const getFormulaValueFieldsConfigured = (formulaValue) => {
    if (!formulaValue?.type) {
        return false;
    }
    if (formulaValue.type === CustomVariableValueType.Absolute) {
        const value = formulaValue;
        const conditions = [value?.type, value?.units, value?.value];
        return conditions.every((condition) => isNotEmpty(condition, true));
    }
    else {
        const value = formulaValue;
        const cut = value?.cut;
        let conditions = getFormulaValueRelativeValueFieldsConfiguredConditions(formulaValue);
        if (cut) {
            if (value?.transformationType === CutType.PointInTime) {
                const cutEvent = cut?.point?.cutEvent;
                const cutEventConditions = getCutEventFilledConditions(cutEvent, cut?.point?.timestampType);
                conditions = [...conditions, ...cutEventConditions];
            }
            else {
                const startCutEvent = cut?.start?.cutEvent;
                const endCutEvent = cut?.end?.cutEvent;
                const startCutEventConditions = getCutEventFilledConditions(startCutEvent, cut?.start?.timestampType);
                const endCutEventConditions = getCutEventFilledConditions(endCutEvent, cut?.end?.timestampType);
                conditions = [...conditions, ...startCutEventConditions, ...endCutEventConditions];
            }
        }
        return conditions.every((condition) => isNotEmpty(condition, true));
    }
};
export const checkIfFormulaReferencingCustomVariable = (formula, customVariableId) => {
    const checkFormulaValue = (formulaValue) => {
        const formulaOf = formulaValue?.of;
        const isCustomVariableValue = formulaOf?.type === CustomVariableRelativeValueVariableType.CustomVariable;
        if (!isCustomVariableValue) {
            return false;
        }
        return formulaOf?.variableId === customVariableId;
    };
    if (isTwoValuesFormula(formula?.type)) {
        return [
            checkFormulaValue(formula?.first),
            checkFormulaValue(formula?.second),
        ].some(Boolean);
    }
    if (isSingleValueFormula(formula?.type)) {
        return checkFormulaValue(formula?.value);
    }
    if (formula?.type === CustomVariableFormulaType.ZScore) {
        return [
            checkFormulaValue(formula?.referenceValue),
            checkFormulaValue(formula?.optimum),
            checkFormulaValue(formula?.spread),
        ].some(Boolean);
    }
    if (formula?.type === CustomVariableFormulaType.SaturationScore) {
        return [
            checkFormulaValue(formula?.referenceValue),
            checkFormulaValue(formula?.referenceLow),
            checkFormulaValue(formula?.referenceHigh),
            checkFormulaValue(formula?.value50),
        ].some(Boolean);
    }
    if (formula?.type === CustomVariableFormulaType.LogisticScore) {
        return [
            checkFormulaValue(formula?.referenceValue),
            checkFormulaValue(formula?.referenceLow),
            checkFormulaValue(formula?.referenceHigh),
            checkFormulaValue(formula?.threshold),
            checkFormulaValue(formula?.sensitivity),
        ].some(Boolean);
    }
    return false;
};
export const getFormulaFieldsConfigured = (values) => {
    const formula = get(values, `${CustomVariableFormFields.formula}`);
    if (isTwoValuesFormula(formula?.type)) {
        return [
            getFormulaValueFieldsConfigured(formula?.first),
            getFormulaValueFieldsConfigured(formula?.second),
        ].every(Boolean);
    }
    if (isSingleValueFormula(formula?.type)) {
        return getFormulaValueFieldsConfigured(formula?.value);
    }
    if (formula?.type === CustomVariableFormulaType.ZScore) {
        return [
            getFormulaValueFieldsConfigured(formula?.referenceValue),
            getFormulaValueFieldsConfigured(formula?.optimum),
            getFormulaValueFieldsConfigured(formula?.spread),
        ].every(Boolean);
    }
    if (formula?.type === CustomVariableFormulaType.SaturationScore) {
        return [
            getFormulaValueFieldsConfigured(formula?.referenceValue),
            getFormulaValueFieldsConfigured(formula?.referenceHigh),
            getFormulaValueFieldsConfigured(formula?.referenceLow),
            getFormulaValueFieldsConfigured(formula?.value50),
        ].every(Boolean);
    }
    if (formula?.type === CustomVariableFormulaType.LogisticScore) {
        return [
            getFormulaValueFieldsConfigured(formula?.referenceValue),
            getFormulaValueFieldsConfigured(formula?.referenceHigh),
            getFormulaValueFieldsConfigured(formula?.referenceLow),
            getFormulaValueFieldsConfigured(formula?.sensitivity),
            getFormulaValueFieldsConfigured(formula?.threshold),
        ].every(Boolean);
    }
    return false;
};
export const getFormulaFieldsErrored = (values, errors) => {
    const formula = get(values, `${CustomVariableFormFields.formula}`);
    if (isTwoValuesFormula(formula?.type)) {
        return [
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.first}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.second}`),
        ].some(Boolean);
    }
    if (isSingleValueFormula(formula?.type)) {
        return !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.value}`);
    }
    if (formula?.type === CustomVariableFormulaType.ZScore) {
        return [
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceValue}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.optimum}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.spread}`),
        ].some(Boolean);
    }
    if (formula?.type === CustomVariableFormulaType.SaturationScore) {
        return [
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceValue}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceHigh}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceLow}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.value50}`),
        ].some(Boolean);
    }
    if (formula?.type === CustomVariableFormulaType.LogisticScore) {
        return [
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceValue}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceHigh}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.referenceLow}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.sensitivity}`),
            !!get(errors, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.threshold}`),
        ].every(Boolean);
    }
    return false;
};
export const getFilteredCustomVariablesList = (list, filter, searchQuery) => {
    const listWithAppliedFiltering = list.filter(({ formula }) => {
        if (filter === CustomVariableUnitFilterType.All) {
            return true;
        }
        const unitCategory = formula?.configuredUnits?.category;
        return filter === CustomVariableUnitFilterType.Complex
            ? unitCategory === ParameterTypesCategoryType.Custom
            : unitCategory !== ParameterTypesCategoryType.Custom;
    });
    const listWithAppliedSearch = listWithAppliedFiltering?.filter(({ formula, name }) => {
        const normalizedQuery = searchQuery.toLowerCase();
        if (!normalizedQuery) {
            return true;
        }
        const isSimpleUnitCategory = formula?.configuredUnits?.category !== ParameterTypesCategoryType.Custom;
        const simpleUnit = formula?.configuredUnits?.value;
        const searchVariants = isSimpleUnitCategory && !!simpleUnit ? parameterUnitsSearchVariants[simpleUnit] : [];
        const unitHtml = parseComplexUnitToHtml(formula?.configuredUnits?.complexValue);
        const matchByComplexUnit = unitHtml?.rawUnitString?.toLowerCase().includes(normalizedQuery);
        const matchBySearchWords = searchVariants.some((variant) => variant.toLowerCase().includes(normalizedQuery));
        const matchByName = name?.toLowerCase().includes(normalizedQuery);
        return matchByName || matchByComplexUnit || matchBySearchWords;
    });
    return listWithAppliedSearch;
};
const getPowerFromHtmlString = (string) => {
    const startIndex = string.indexOf("'>");
    const endIndex = string.lastIndexOf("</s");
    return parseInt(string.slice(startIndex + 2, endIndex));
};
const convertDenominatorToNumerator = (string) => {
    const startIndex = string.indexOf("<s");
    const endIndex = string.lastIndexOf("n>");
    const power = getPowerFromHtmlString(string);
    const textToReplace = string.slice(startIndex, endIndex + 2);
    return isNaN(power)
        ? `${string}${superscriptValue(-1)}`
        : string.replace(textToReplace, superscriptValue(-power));
};
const getCombinedUnitPart = (part, searchQuery) => part.reduce((acc, item) => {
    const shouldAddPower = ![0, 1].includes(item?.p);
    if (typeof item === "string") {
        const text = searchQuery ? highlightMatchingText(item, searchQuery) : item;
        return acc ? `${acc}*${text}` : text;
    }
    const unitHtml = getUnitHtml(item, searchQuery);
    const itemToAdd = shouldAddPower && !!unitHtml ? `${unitHtml}${superscriptValue(item?.p)}` : unitHtml;
    if (!acc) {
        return itemToAdd;
    }
    else {
        return !itemToAdd ? acc : `${acc}*${itemToAdd}`;
    }
}, "");
const getUnitHtml = (unit, searchQuery) => {
    const numerator = getCombinedUnitPart(unit?.n, searchQuery);
    const denominator = getCombinedUnitPart(unit?.d, searchQuery);
    if (!numerator && !!denominator) {
        return `${convertDenominatorToNumerator(denominator)}`;
    }
    return `${numerator ?? ""}${denominator ? `/${denominator}` : ""}`;
};
export const parseComplexUnitToHtml = (unitJson, searchQuery) => {
    if (!unitJson) {
        return { html: "" };
    }
    const unitObject = unitJson ? JSON.parse(unitJson) : "";
    if (!unitObject) {
        return { html: "" };
    }
    const shouldAddPower = ![0, 1].includes(unitObject?.p);
    let html = getUnitHtml(unitObject, searchQuery);
    if (shouldAddPower) {
        html = `${html}${superscriptValue(unitObject?.p)}`;
    }
    return {
        html,
        rawUnitString: sanitizeHtmlTags(html),
    };
};
export const getDetailedFormulaUnits = (formula) => {
    if (!formula) {
        return "";
    }
    const { parameters } = { ...formula?.configuredUnits };
    const getParameterUnitByName = (name) => {
        const value = parameters?.find((item) => item.name === name)?.value ?? "";
        return parseComplexUnitToHtml(value)?.html;
    };
    const a = getParameterUnitByName("a");
    const b = getParameterUnitByName("b");
    const reference = getParameterUnitByName("reference");
    const optimum = getParameterUnitByName("optimum");
    const spread = getParameterUnitByName("spread");
    const y0 = getParameterUnitByName("referenceLow");
    const yInfinity = getParameterUnitByName("referenceHigh");
    const x = getParameterUnitByName("referenceValue");
    const k = getParameterUnitByName("Value50");
    const t = getParameterUnitByName("threshold");
    const s = getParameterUnitByName("sensitivity");
    switch (formula?.type) {
        case CustomVariableFormulaType.Sum:
            return `${a} + ${b}`;
        case CustomVariableFormulaType.Difference:
            return `${a} - ${b}`;
        case CustomVariableFormulaType.Product:
            return `${a}*${b}`;
        case CustomVariableFormulaType.Power:
            return `${a}^${b}`;
        case CustomVariableFormulaType.Quotient:
            return `${a}/${b}`;
        case CustomVariableFormulaType.Maximum:
            return `max(${a}, ${b})`;
        case CustomVariableFormulaType.Minimum:
            return `min(${a}, ${b})`;
        case CustomVariableFormulaType.Exponential:
            return `exp(${a})`;
        case CustomVariableFormulaType.Ln:
            return `ln(${a})`;
        case CustomVariableFormulaType.LogBase10:
            return `log<span style="font-size: 0.7em;position: relative;top: 1em;">10</span>(${a})`;
        case CustomVariableFormulaType.AbsoluteDifference:
            return `abs(${a} - ${b})`;
        case CustomVariableFormulaType.ZScore:
            return `${reference} - ${optimum}/${spread}`;
        case CustomVariableFormulaType.SaturationScore:
            return `${y0} + (${yInfinity} - ${y0})*${x}/${k} + ${x}`;
        case CustomVariableFormulaType.LogisticScore:
            return `${y0} + (${yInfinity} - ${y0})*logistic(${x}, ${t}, ${s})`;
        default:
            return "";
    }
};
export const getCustomVariableOptions = (usedRecipeIds, recipes, filterFn) => {
    const customVariables = getRecipesCustomVariables(recipes, usedRecipeIds, (variable) => Boolean(variable.recordInData) && (!filterFn || filterFn(variable)));
    const customVariablesOptions = customVariables.map(({ id, name, formula, recipeId }) => ({
        value: id,
        type: "Custom",
        recipeId,
        units: !!formula?.configuredUnits && !!formula?.configuredUnits?.isSuccess
            ? formula?.configuredUnits?.complexValue
            : "",
        label: name,
        isGroupName: false,
    }));
    return [
        {
            value: CustomVariableRelativeValueVariableType.CustomVariable,
            label: "Custom Variables",
            isGroupName: true,
            disabled: true,
            recipeId: NaN,
        },
        ...customVariablesOptions,
    ];
};
export const getCustomVariableStatus = (customVariable) => {
    const formulaFieldsConfigured = getFormulaFieldsConfigured(customVariable);
    const errored = !!customVariable?.formula?.configuredUnits && customVariable?.formula?.configuredUnits?.error;
    if (!formulaFieldsConfigured && !errored) {
        return CustomVariableValueStatus.Default;
    }
    if (errored) {
        return CustomVariableValueStatus.Errored;
    }
    if (customVariable?.formula?.complete) {
        return CustomVariableValueStatus.Completed;
    }
    return CustomVariableValueStatus.Default;
};
